import { App, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import {
  NOTIFICATION_TYPE_LABELS,
  notificationTypeTagClass,
} from '@/constants/notification';
import { ApiError } from '@/services/api/client';
import * as notificationsApi from '@/services/api/notifications';
import type { NotificationListItem } from '@/types/notification';
import { resolveNotificationTarget, notificationActionLabel } from '@/utils/notification-navigation';
import { formatDateTime } from '@/utils/format-date';

interface NotificationDetailMobilePageProps {
  variant: 'leader' | 'employee';
}

export function NotificationDetailMobilePage({ variant }: NotificationDetailMobilePageProps) {
  const { receiverId } = useParams<{ receiverId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState<NotificationListItem | null>(null);

  const load = useCallback(async () => {
    if (!receiverId) {
      return;
    }
    setLoading(true);
    try {
      const detail = await notificationsApi.fetchNotification(receiverId);
      setItem(detail);

      if (detail.readStatus === 'UNREAD') {
        const updated = await notificationsApi.markNotificationRead(receiverId);
        setItem(updated);
      }
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载消息详情失败';
      message.error(text);
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [message, receiverId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!receiverId) {
    return <MobileEmpty message="无效的消息" />;
  }

  if (loading) {
    return (
      <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  if (!item) {
    return <MobileEmpty message="消息不存在或无权查看" />;
  }

  const targetPath = resolveNotificationTarget(variant, item);

  return (
    <div className="mobile-detail-page">
      <div className="mobile-card mobile-card--flat">
        <div className="mobile-card__header" style={{ marginBottom: 8 }}>
          <span className="mobile-card__title">{item.title}</span>
          <span className={notificationTypeTagClass(item.type)}>
            {NOTIFICATION_TYPE_LABELS[item.type]}
          </span>
        </div>
        <p className="mobile-notification-card__content">{item.content}</p>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">时间</span>
          <span className="mobile-detail-row__value">{formatDateTime(item.createdAt)}</span>
        </div>
        <div className="mobile-detail-row">
          <span className="mobile-detail-row__label">状态</span>
          <span className="mobile-detail-row__value">
            {item.readStatus === 'UNREAD' ? '未读' : '已读'}
          </span>
        </div>
        {item.actor ? (
          <div className="mobile-detail-row">
            <span className="mobile-detail-row__label">来自</span>
            <span className="mobile-detail-row__value">{item.actor.name}</span>
          </div>
        ) : null}
      </div>

      {targetPath ? (
        <footer className="mobile-detail-footer">
          <button
            type="button"
            className="mobile-btn mobile-btn--primary mobile-btn--block"
            onClick={() => navigate(targetPath)}
          >
            {notificationActionLabel(item)}
          </button>
        </footer>
      ) : null}
    </div>
  );
}
